import { useState, type FormEvent, type ReactNode } from "react";
import { api, ApiError } from "../lib/api.js";
import { hashForRoute } from "../lib/hashRoute.js";
import { buildNewBookRequests, DEFAULT_PAGE_COUNT, type NewBookForm } from "../lib/newBook.js";
import type { Deliverable, Item, Project } from "../lib/types.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const STEP_LABELS = [
  "Create the project",
  "Set the book defaults",
  "Add the outline item",
  "Add the outline deliverable",
  "Start the outline generation",
] as const;

type StepState = "pending" | "running" | "done" | "error";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {hint && <span className="mb-1 block text-xs text-gray-500 dark:text-gray-400">{hint}</span>}
      {children}
    </label>
  );
}

/** The "New picture book" wizard (Picture-Book Workflow plan, Task 3.1).
 *  Orchestration only: turns a short intake form into a project + an outline
 *  item + a kicked-off outline generation by calling existing endpoints in
 *  sequence, then drops the owner into the outline's item page to review it. */
export function NewBook() {
  const [form, setForm] = useState<NewBookForm>({
    title: "",
    premise: "",
    characters: "",
    ageRange: "3-5",
    pageCount: DEFAULT_PAGE_COUNT,
    artStyle: "",
    avoid: "",
  });
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState<StepState[]>(() => STEP_LABELS.map(() => "pending"));
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof NewBookForm>(key: K, value: NewBookForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const canSubmit =
    form.title.trim() !== "" && form.premise.trim() !== "" && form.artStyle.trim() !== "";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || busy) return;

    const reqs = buildNewBookRequests(form);
    const marks = STEP_LABELS.map(() => "pending" as StepState);
    setBusy(true);
    setError(null);
    setSteps([...marks]);

    const advance = (i: number, state: StepState) => {
      marks[i] = state;
      setSteps([...marks]);
    };

    try {
      advance(0, "running");
      const project = await api<Project>("/api/projects", { method: "POST", json: reqs.project });
      advance(0, "done");

      advance(1, "running");
      await api<Project>(`/api/projects/${project.projectId}`, {
        method: "PATCH",
        json: reqs.projectDefaults,
      });
      advance(1, "done");

      advance(2, "running");
      const itemsRes = await api<{ items: Item[] }>(`/api/projects/${project.projectId}/items`, {
        method: "POST",
        json: { items: [reqs.outlineItem] },
      });
      const outlineItem = itemsRes.items[0];
      if (!outlineItem) throw new Error("The outline item was not created.");
      advance(2, "done");

      advance(3, "running");
      const deliverable = await api<Deliverable>(
        `/api/projects/${project.projectId}/items/${outlineItem.itemId}/deliverables`,
        { method: "POST", json: reqs.outlineDeliverable },
      );
      advance(3, "done");

      advance(4, "running");
      await api(`/api/deliverables/${deliverable.deliverableId}/generate`, {
        method: "POST",
        json: {},
      });
      advance(4, "done");

      location.hash = hashForRoute({
        kind: "itemDetail",
        projectId: project.projectId,
        itemId: outlineItem.itemId,
      });
    } catch (err) {
      const failedAt = marks.findIndex((s) => s === "running");
      if (failedAt !== -1) advance(failedAt, "error");
      const step = failedAt !== -1 ? `"${STEP_LABELS[failedAt]}" failed: ` : "";
      const msg =
        err instanceof ApiError && err.code
          ? `${err.code} - ${err.message}`
          : err instanceof Error
            ? err.message
            : "Something went wrong.";
      setError(
        `${step}${msg}` +
          (failedAt > 0 ? " The partial project is on the Projects page; delete it if you want to start over." : ""),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New picture book</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Fill this in and the pipeline writes the whole story once. You review and regenerate the
        outline until it is right, then fan it out into one illustrated page per spread.
      </p>

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-6 space-y-4">
        <Field label="Title">
          <input
            className={inputCls}
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="The Sleepy Fox"
          />
        </Field>

        <Field label="Premise" hint="One or two sentences on what the book is about.">
          <textarea
            className={inputCls}
            rows={3}
            value={form.premise}
            onChange={(e) => set("premise", e.target.value)}
          />
        </Field>

        <Field label="Characters" hint="One per line, as `Name - description`.">
          <textarea
            className={inputCls}
            rows={4}
            value={form.characters}
            onChange={(e) => set("characters", e.target.value)}
            placeholder={"Rusty - a small orange fox\nOwl - a wise old barn owl"}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Age range">
            <input
              className={inputCls}
              value={form.ageRange}
              onChange={(e) => set("ageRange", e.target.value)}
              placeholder="3-5"
            />
          </Field>
          <Field label="Page count">
            <input
              type="number"
              min={1}
              className={inputCls}
              value={form.pageCount}
              onChange={(e) => set("pageCount", e.target.value)}
            />
          </Field>
        </div>

        <Field label="Art style" hint="Medium, palette, mood - the look every page shares.">
          <textarea
            className={inputCls}
            rows={3}
            value={form.artStyle}
            onChange={(e) => set("artStyle", e.target.value)}
            placeholder="soft watercolour, warm palette, gentle rounded shapes"
          />
        </Field>

        <Field label="Look and feel to avoid" hint="Optional.">
          <textarea
            className={inputCls}
            rows={2}
            value={form.avoid}
            onChange={(e) => set("avoid", e.target.value)}
          />
        </Field>

        <button type="submit" disabled={!canSubmit || busy} className={btnPrimary}>
          {busy ? "Working..." : "Create book"}
        </button>
      </form>

      {(busy || steps.some((s) => s !== "pending")) && (
        <ol className="mt-6 space-y-1 text-sm">
          {STEP_LABELS.map((label, i) => {
            const s = steps[i];
            const mark =
              s === "done" ? "[done]" : s === "running" ? "[...]" : s === "error" ? "[failed]" : "[ ]";
            const cls =
              s === "done"
                ? "text-green-600 dark:text-green-400"
                : s === "error"
                  ? "text-red-600 dark:text-red-400"
                  : s === "running"
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-400 dark:text-gray-500";
            return (
              <li key={label} className={cls}>
                <span className="inline-block w-16 font-mono">{mark}</span>
                {label}
              </li>
            );
          })}
        </ol>
      )}

      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
